import { useEffect, useMemo, useState } from 'react';
import { clampWindow, getDefaultWindow } from '@/utils/chartWindow';

interface UseChartWindowOptions {
  preferredVisiblePoints?: number;
  defaultStartIndex?: number;
  defaultEndIndex?: number;
}

export const useChartWindow = (
  totalPoints: number,
  options: UseChartWindowOptions = {},
) => {
  const { preferredVisiblePoints, defaultStartIndex, defaultEndIndex } = options;

  const defaultWindow = useMemo(
    () => {
      if (defaultStartIndex !== undefined || defaultEndIndex !== undefined) {
        return clampWindow(
          totalPoints,
          defaultStartIndex ?? 0,
          defaultEndIndex ?? Math.max(totalPoints - 1, 0),
        );
      }
      return getDefaultWindow(totalPoints, preferredVisiblePoints);
    },
    [totalPoints, preferredVisiblePoints, defaultStartIndex, defaultEndIndex],
  );

  const [startIndex, setStartIndex] = useState(defaultWindow.startIndex);
  const [endIndex, setEndIndex] = useState(defaultWindow.endIndex);

  useEffect(() => {
    setStartIndex(defaultWindow.startIndex);
    setEndIndex(defaultWindow.endIndex);
  }, [defaultWindow.startIndex, defaultWindow.endIndex]);

  const setWindow = (nextStart: number, nextEnd: number) => {
    const next = clampWindow(totalPoints, nextStart, nextEnd);
    setStartIndex(next.startIndex);
    setEndIndex(next.endIndex);
  };

  return {
    startIndex,
    endIndex,
    setWindow,
    hasWindowing: totalPoints > 1,
  };
};

export default useChartWindow;